import { AssetRecordType, createShapeId } from "@tldraw/tlschema";
import type { BoxModel, Editor } from "./canvas";
import type { TLImageShape, TLShapeId } from "@tldraw/tlschema";
import type { Trace } from "../shared/schema";
import { areaBounds, captureSize, type AreaPoint } from "../shared/trace-area";
import { editableTarget } from "./tracing";
import { uploadFile, type Room } from "./api";

/** Capture artwork, including imported plan previews, but not empty sheet surfaces. */
export function areaSources(editor: Editor, trace: Trace, bounds: BoxModel) {
  const sorted = editor.getCurrentPageShapesSorted();
  const below = sorted.slice(
    0,
    sorted.findIndex((s) => s.id === trace.id),
  );
  return below.filter(
    (s) =>
      s.type !== "trace" &&
      !(s.type === "sheet" && !s.props.preview) &&
      !editor.isShapeHidden(s) &&
      editor.getShapePageBounds(s)?.collides(bounds),
  );
}

/** Render the composition once, mask it, and persist an independent image on the paper. */
export async function copyAreaToPaper(
  editor: Editor,
  room: Room,
  traceId: TLShapeId,
  points: AreaPoint[],
  signal: AbortSignal,
) {
  const trace = editor.getShape<Trace>(traceId);
  if (!trace || trace.type !== "trace" || !editableTarget(editor, trace))
    throw new Error("This tracing sheet is no longer editable.");
  const rect = areaBounds(points);
  const bounds = editor.getShapePageBounds(trace)?.clone();
  if (!bounds) throw new Error("The tracing sheet is unavailable.");
  Object.assign(bounds, rect);
  const sources = areaSources(editor, trace, bounds);
  if (!sources.length)
    throw new Error(
      "There is nothing underneath this area. Move the paper over a drawing first.",
    );
  const ids = sources.map((s) => s.id);
  const included = [...editor.getShapeAndDescendantIds(ids)];
  if (included.includes(traceId))
    throw new Error("Lift the tracing sheet before copying an area.");
  if (
    included.filter((id) => editor.getShape(id)?.type === "image").length > 50
  )
    throw new Error("Select an area with at most 50 source images.");
  for (const id of included) {
    const source = editor.getShape(id);
    if (source?.type === "image") {
      const asset =
        source.props.assetId && editor.getAsset(source.props.assetId);
      if (!asset || !asset.props.src)
        throw new Error("Wait for the source images to finish uploading.");
    }
  }
  // Empty sheets are excluded from the image, but their transforms/visibility
  // still affect their contents. Detect changes to those parents during capture.
  const tracked = new Set(included);
  for (const id of included) {
    let parent = editor.getShape(id)?.parentId;
    while (parent?.startsWith("shape:")) {
      const parentId = parent as TLShapeId;
      if (tracked.has(parentId)) break;
      tracked.add(parentId);
      parent = editor.getShape(parentId)?.parentId;
    }
  }
  const signature = () =>
    JSON.stringify(
      [...tracked].map((id) => {
        const s = editor.getShape(id);
        return [
          s,
          s?.type === "image" && s.props.assetId
            ? editor.getAsset(s.props.assetId)
            : null,
        ];
      }),
    );
  const original = signature();
  const placement = (t: Trace) =>
    JSON.stringify([t.x, t.y, t.rotation, t.parentId, t.props.w, t.props.h]);
  const originalPlacement = placement(trace);
  const check = () => {
    signal.throwIfAborted();
    const live = editor.getShape<Trace>(traceId);
    if (!live || live.type !== "trace" || !editableTarget(editor, live))
      throw new Error("This tracing sheet is no longer editable.");
    if (
      placement(live) !== originalPlacement ||
      signature() !== original ||
      JSON.stringify(areaSources(editor, live, bounds).map((s) => s.id)) !==
        JSON.stringify(ids)
    )
      throw new Error("The drawing or paper changed. Select the area again.");
    return live;
  };
  check();
  // The pinned SDK omits a sole frame's own artwork on export. Repeating its ID
  // keeps an imported plan preview; the SDK's descendant set renders it only once.
  const exportIds =
    ids.length === 1 && editor.isShapeFrameLike(sources[0])
      ? [ids[0], ids[0]]
      : ids;
  const size = captureSize(bounds.w, bounds.h);
  const rendered = await editor.toImage(exportIds, {
    format: "png",
    bounds,
    padding: 0,
    background: false,
    scale: size.scale,
    pixelRatio: 1,
  });
  check();
  const bitmap = await createImageBitmap(rendered.blob);
  const canvas = document.createElement("canvas");
  let blob: Blob;
  try {
    canvas.width = size.w;
    canvas.height = size.h;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Image capture is unavailable in this browser.");
    ctx.beginPath();
    points.forEach((p, i) => {
      const x = ((p.x - bounds.x) * size.w) / bounds.w,
        y = ((p.y - bounds.y) * size.h) / bounds.h;
      if (i) ctx.lineTo(x, y);
      else ctx.moveTo(x, y);
    });
    ctx.closePath();
    ctx.clip("evenodd");
    ctx.drawImage(bitmap, 0, 0, size.w, size.h);
    blob = await new Promise<Blob>((resolve, reject) =>
      canvas.toBlob(
        (b) =>
          b ? resolve(b) : reject(new Error("Could not capture this area.")),
        "image/png",
      ),
    );
  } finally {
    bitmap.close();
    canvas.width = canvas.height = 1;
  }
  check();
  const name = `${trace.props.name} — copied area.png`;
  const uploaded = await uploadFile(
    room.id,
    new File([blob], name, { type: "image/png" }),
    signal,
    room.platform === "cloudflare",
  );
  const live = check();
  if (!uploaded.image)
    throw new Error("The copied image could not be saved. Try again.");
  const assetId = AssetRecordType.createId(),
    id = createShapeId();
  const p = editor.getPointInShapeSpace(live, bounds);
  editor.markHistoryStoppingPoint("Copy area as image");
  editor.run(() => {
    editor.createAssets([
      AssetRecordType.create({
        id: assetId,
        type: "image",
        props: {
          name,
          src: uploaded.src,
          w: uploaded.image!.w,
          h: uploaded.image!.h,
          mimeType: "image/png",
          isAnimated: false,
        },
        meta: { imagePreviews: true },
      }),
    ]);
    editor.createShape<TLImageShape>({
      id,
      type: "image",
      parentId: traceId,
      x: p.x,
      y: p.y,
      rotation: -editor.getShapePageTransform(live).rotation(),
      props: { assetId, w: bounds.w, h: bounds.h, altText: "Copied area" },
      meta: {
        metersPerUnit: 0,
        calibrationWidth: bounds.w,
        calibration: "Not to Scale",
      },
    });
    editor.select(traceId);
  });
  return id;
}
